/* eslint-disable max-len */
import React from 'react';

/**
 * Defines the structure of the credit card object used for rendering the UI block
 */
export interface Card {
    salesProductCode: string;
    thumbnail: string;
    cardName: string;
    purchaseRate: {
        heading: string;
        value: string;
    };
    creditLimit: {
        heading: string;
        value: string;
    };
    monthlyFee: {
        heading: string;
        value: string;
    };
    intTranFee: {
        heading: string;
        value: string;
    };
    benefits: string;
    'trade-offs': string;
    learnMore: string;
    offers?: string;
    applyButtonText: {
        web: string;
        mobile: string;
    };
    specialOffers: string;
    doubleDating?: {
        ddHeading: string;
        ddSubHeading: string;
        interestRate: string;
        monthlyFee: string;
        ddCloseBtn: string;
    };
}

interface CardDetailsProps {
    card: Card;
}

/**
 * Renders a credit card UI block using data from JSON
 * Follows the exact structure of cardsV3 template from commbank-lego repo
 * Displays the card in the same structure as the deprecated experience fragment
 * Using dangerouslySetInnerHTML because the fields contain HTML markup
 */
const CardDetails: React.FC<CardDetailsProps> = ({ card }) => {
    const createMarkup = (html: string) => ({ __html: html || '' });

    return (
        <div className="cardsV3">
            <div className="new-card-container" id={card?.salesProductCode}>
                <div className="card-image-block">
                    <img className="card-image" src={card?.thumbnail} aria-hidden="true" alt="" />
                </div>
                <div>
                    <p className="card-name">{card?.cardName}</p>
                </div>
                <div className="purchase-rate">
                    <dl>
                        <dt dangerouslySetInnerHTML={createMarkup(card?.purchaseRate?.heading)} />
                        <dd dangerouslySetInnerHTML={createMarkup(card?.purchaseRate?.value)} />
                    </dl>
                </div>
                <div className="credit-limit">
                    <dl>
                        <dt dangerouslySetInnerHTML={createMarkup(card?.creditLimit?.heading)} />
                        <dd dangerouslySetInnerHTML={createMarkup(card?.creditLimit?.value)} />
                    </dl>
                </div>
                <div className="monthly-fee">
                    <dl>
                        <dt dangerouslySetInnerHTML={createMarkup(card?.monthlyFee?.heading)} />
                        <dd dangerouslySetInnerHTML={createMarkup(card?.monthlyFee?.value)} />
                    </dl>
                </div>
                <div className="int-tran-fee">
                    <dl>
                        <dt dangerouslySetInnerHTML={createMarkup(card?.intTranFee?.heading)} />
                        <dd dangerouslySetInnerHTML={createMarkup(card?.intTranFee?.value)} />
                    </dl>
                </div>
                <div className="section-divider" />
                <div className="benefits" dangerouslySetInnerHTML={createMarkup(card?.benefits)} />
                <div className="trade-offs" dangerouslySetInnerHTML={createMarkup(card?.['trade-offs'])} />
                <div className="learn-more" dangerouslySetInnerHTML={createMarkup(card?.learnMore)} />
                <div className="section-divider" />

                {card?.offers && (
                    <div className="offer row">
                        <div className="icon-message-offer offer-icon col-sm-1 col-1" />
                        <div className="offer-text col-sm-11 col-11" dangerouslySetInnerHTML={createMarkup(card?.offers)} />
                    </div>
                )}

                <div className="apply-button">
                    <button type="button" className="button-emphasis" id="apply-now">
                        <span className="lg-view">{card?.applyButtonText?.web}</span>
                        <span className="sm-view">{card?.applyButtonText?.mobile}</span>
                    </button>
                </div>

                <div className="offer-special" dangerouslySetInnerHTML={createMarkup(card?.specialOffers)} />

                <div className="dd-popup row" hidden>
                    <div className="dd-popup-content col-sm-10 col-10">
                        <div className="dd-heading">
                            <h3>{card?.doubleDating?.ddHeading || ''}</h3>
                        </div>
                        <div className="dd-subheading">
                            {card?.doubleDating?.ddSubHeading || ''}
                        </div>
                        <section data-class="interest-rate">
                            <div className="card-section" dangerouslySetInnerHTML={createMarkup(card?.doubleDating?.interestRate || '')} />
                        </section>
                        <section data-class="monthly-fee">
                            <div className="card-section" dangerouslySetInnerHTML={createMarkup(card?.doubleDating?.monthlyFee || '')} />
                        </section>
                        <div className="button-emphasis">{card?.doubleDating?.ddCloseBtn || ''}</div>
                    </div>
                    <div>
                        <button type="button" className="icon-close-button cross-button" aria-label="Close popup" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CardDetails;
